/**
 * Preview as a member (MOSA-845).
 *
 * The API decides the tabs and the rows. This file only reads that answer
 * and decides which requests carry the preview id.
 */

export const PREVIEW_PARAM = "previewAs";

export type PreviewLevel = "VIEW" | "EDIT";

export type PreviewTab = {
  tab: string;
  level: PreviewLevel;
  rowCount: number;
  ruleBroken: boolean;
};

export type PreviewView = {
  memberId: string;
  email: string;
  access: string;
  tabs: PreviewTab[];
};

let activePreviewMemberId: string | null = null;

export const setActivePreviewMemberId = (id: string | null): void => {
  const next = typeof id === "string" ? id.trim() : "";
  activePreviewMemberId = next || null;
};

export const getActivePreviewMemberId = (): string | null =>
  activePreviewMemberId;

/**
 * Cache identity for a data read.
 * Blank and missing are the owner's own view, and they must not share a
 * cached list with a member preview.
 */
export const previewCacheToken = (
  memberId: string | null | undefined,
): string => {
  if (typeof memberId !== "string") return "";
  const id = memberId.trim();
  return id;
};

const NAME_PREFIX = "onlydash.previewName.";

export const rememberPreviewName = (memberId: string, name: string): void => {
  const label = name.trim();
  if (!memberId.trim() || !label) return;
  try {
    sessionStorage.setItem(NAME_PREFIX + memberId.trim(), label);
  } catch {
    // A private window can refuse storage. The banner then uses the email.
  }
};

export const rememberedPreviewName = (memberId: string): string | null => {
  try {
    const value = sessionStorage.getItem(NAME_PREFIX + memberId);
    return value && value.trim() ? value.trim() : null;
  } catch {
    return null;
  }
};

export const previewIdFromParams = (
  params: URLSearchParams,
): string | null => {
  const raw = params.get(PREVIEW_PARAM);
  if (raw == null) return null;
  const id = raw.trim();
  return id || null;
};

export const previewIdFromLocation = (loc: {
  search?: string;
  hash?: string;
}): string | null => {
  const fromSearch = previewIdFromParams(
    new URLSearchParams(loc.search ?? ""),
  );
  if (fromSearch) return fromSearch;
  const hash = loc.hash ?? "";
  const queryAt = hash.indexOf("?");
  if (queryAt === -1) return null;
  return previewIdFromParams(new URLSearchParams(hash.slice(queryAt + 1)));
};

export const datasourceIdFromPath = (pathname: string): string | null => {
  const match = pathname.match(/^\/datasources\/([^/]+)/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

const pathOnly = (path: string): string => {
  const noHash = path.split("#")[0];
  const noQuery = noHash.split("?")[0];
  if (noQuery.length > 1 && noQuery.endsWith("/")) return noQuery.slice(0, -1);
  return noQuery;
};

/** Data reads carry the id. Config calls and writes do not. */
export const previewSignalOn = (method: string, path: string): boolean => {
  if (method.toUpperCase() !== "GET" && method.toUpperCase() !== "HEAD") {
    return false;
  }
  const parts = pathOnly(path).split("/").filter(Boolean);
  if (parts[0] === "data" && parts[1] === "aggregate" && parts.length === 4) {
    return true;
  }
  if (parts[0] === "data" && parts[1] !== "aggregate" && parts.length >= 3 && parts.length <= 4) {
    return true;
  }
  return (
    parts[0] === "datasources" && parts.length === 3 && parts[2] === "preview"
  );
};

export const hrefWithPreview = (
  path: string,
  memberId: string | null,
): string => {
  const id = memberId?.trim() ?? "";
  if (!id) return path;
  const hashAt = path.indexOf("#");
  const hash = hashAt === -1 ? "" : path.slice(hashAt);
  const beforeHash = hashAt === -1 ? path : path.slice(0, hashAt);
  const queryAt = beforeHash.indexOf("?");
  const pathname = queryAt === -1 ? beforeHash : beforeHash.slice(0, queryAt);
  const params = new URLSearchParams(
    queryAt === -1 ? "" : beforeHash.slice(queryAt + 1),
  );
  params.set(PREVIEW_PARAM, id);
  return `${pathname}?${params.toString()}${hash}`;
};

const isLevel = (value: unknown): value is PreviewLevel =>
  value === "VIEW" || value === "EDIT";

const parseTab = (value: unknown): PreviewTab | null => {
  if (!value || typeof value !== "object") return null;
  const row = value as Record<string, unknown>;
  const tab = typeof row.tab === "string" ? row.tab.trim() : "";
  if (!tab || !isLevel(row.level)) return null;
  if (typeof row.rowCount !== "number" || !Number.isFinite(row.rowCount)) {
    return null;
  }
  return {
    tab,
    level: row.level,
    rowCount: row.rowCount,
    ruleBroken: row.ruleBroken === true,
  };
};

/** The preview body, or null when the body is not that contract. */
export const parsePreviewBody = (body: unknown): PreviewView | null => {
  if (!body || typeof body !== "object") return null;
  const row = body as Record<string, unknown>;
  const memberId = typeof row.memberId === "string" ? row.memberId.trim() : "";
  const email = typeof row.email === "string" ? row.email.trim() : "";
  const access = typeof row.access === "string" ? row.access.trim() : "";
  if (!memberId || !email || !access || !Array.isArray(row.tabs)) return null;
  return {
    memberId,
    email,
    access,
    tabs: row.tabs
      .map(parseTab)
      .filter((tab): tab is PreviewTab => tab !== null),
  };
};

export const collectionsFromPreview = <
  T extends { id?: string | null; name?: string | null },
>(
  schema: T[],
  tabs: PreviewTab[],
): Array<T & { name: string; previewRowCount: number }> =>
  tabs.map((tab) => {
    const match = schema.find((row) => row.name === tab.tab);
    const base = (match ?? { name: tab.tab }) as T;
    return { ...base, name: tab.tab, previewRowCount: tab.rowCount };
  });

export const tabRowCountLabel = (count: number): string =>
  count === 1 ? "1 row" : `${count} rows`;

export const previewButtonLabel = (name: string): string =>
  `Preview as ${name}`;

export const previewBannerText = (name: string): string =>
  `You are previewing ${name}.`;

export const previewReadOnlyText = (): string => "This view is read-only.";

/** The API sentence on a preview 403. Other errors stay with their caller. */
export const previewFailureMessage = (error: unknown): string | null => {
  const response = (
    error as { response?: { status?: unknown; data?: { message?: unknown } } }
  )?.response;
  if (response?.status !== 403) return null;
  const message = response.data?.message;
  if (typeof message !== "string") return null;
  const text = message.trim();
  return text || null;
};
